/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { EDIT_DROPIN_TYPE, EDIT_CLIENT_NAME } from "constants/constants";
import { isMobileClassicViewer } from "utils/AppUtils";
import { getShouldRenderAcroForm } from "selectors/documentSelectors";
import { getShouldUseMobileModernViewer, getIsMultiDocModeEnabled } from "selectors/appSelectors";

export const getShouldRenderCommentsDropin = stores => stores.overlayDropinStore.shouldRenderCommentsDropin;
export const getShouldRenderPersonalCommentsDropin = stores =>
  stores.overlayDropinStore.shouldRenderPersonalCommentsDropin &&
  !getShouldRenderHighlightsDropin(stores) &&
  !getShouldRenderCommentsDropin(stores) &&
  !isMobileClassicViewer(getShouldUseMobileModernViewer(stores));
export const getShouldEnableVoiceAnnotations = stores => stores.overlayDropinStore.shouldEnableVoiceAnnotations;
export const getShouldRenderHighlightsDropin = stores => stores.overlayDropinStore.shouldRenderHighlightsDropin;
export const getShouldRenderEditDropin = stores => stores.overlayDropinStore.shouldRenderEditDropin;
export const getShouldPreloadEditModule = stores => stores.overlayDropinStore.shouldPreloadEditModule;
export const getIsDemoMode = stores => stores.overlayDropinStore.isDemoMode;
export const getShowEditOnTextSelection = stores => stores.overlayDropinStore.showEditOnTextSelection;
export const getShouldRenderCropPagesDropin = stores => stores.overlayDropinStore.shouldRenderCropPagesDropin;
export const getShouldRenderNumberPagesDropin = stores => stores.overlayDropinStore.shouldRenderNumberPagesDropin;
export const getShouldRenderSignAuthoringDropin = stores => stores.overlayDropinStore.shouldRenderSignAuthoringDropin;
export const getShouldRenderSignAuthoring2Dropin = stores => stores.overlayDropinStore.shouldRenderSignAuthoring2Dropin;
export const getShouldRenderTroveDropin = stores => stores.overlayDropinStore.shouldRenderTroveDropin;
export const getShouldRenderFillSignDropin = stores => stores.overlayDropinStore.shouldRenderFillSignDropin;
export const getShouldRenderPdfRequestSignaturesDropin = stores =>
  stores.overlayDropinStore.shouldRenderPdfRequestSignaturesDropin;
export const getShouldRenderVisualizerDropin = stores => stores.overlayDropinStore.shouldRenderVisualizerDropin;
export const getShouldRenderESignOverlayDropin = stores => stores.overlayDropinStore.shouldRenderESignOverlayDropin;
export const getPageOverlays = stores => stores.overlayDropinStore.pageOverlays;
export const getCursorStateChange = stores => stores.overlayDropinStore.cursorChanged;
export const getOverlayDropins = stores => stores.overlayDropinStore.overlayDropins;
export const getCurrentCursor = stores => stores.overlayDropinStore.currentCursor;
export const getShouldEnableEncryptedWrite = stores => stores.overlayDropinStore.shouldUseEncryptedWrite;
export const isDropinLoaded = (name, stores) => stores.overlayDropinStore.isDropinLoaded[name] === true;
export const isDropinLoading = (name, stores) => stores.overlayDropinStore.isDropinLoading[name] === true;
export const getOverlayDropinLoadedPromise = (stores, dropinName) => {
  const promiseMap = stores.overlayDropinStore.dropinLoadedPromises;
  if (promiseMap && promiseMap.has(dropinName)) return promiseMap.get(dropinName);
  return null;
};

export const getAcroformDropinMountedPromise = stores => stores.overlayDropinStore.acroformDropinMountedPromise;

export const getShouldRenderDropin = (name, stores) => {
  let shouldRenderDropin = false;
  switch (name) {
    case "comments":
      shouldRenderDropin = getShouldRenderCommentsDropin(stores) || getShouldRenderPersonalCommentsDropin(stores);
      break;
    case "dci-highlights":
      shouldRenderDropin = getShouldRenderHighlightsDropin(stores);
      break;
    case "fillsignoverlay":
      shouldRenderDropin = getShouldRenderFillSignDropin(stores);
      break;
    case "trove":
      shouldRenderDropin = getShouldRenderTroveDropin(stores);
      break;
    case "acroform":
      shouldRenderDropin = getShouldRenderAcroForm(stores);
      break;
    case "visualizer":
      shouldRenderDropin = getShouldRenderVisualizerDropin(stores);
      break;
    case "sign-authoring-overlay":
      shouldRenderDropin = getShouldRenderSignAuthoringDropin(stores);
      break;
    case "sign-authoring2-overlay":
      shouldRenderDropin = getShouldRenderSignAuthoring2Dropin(stores);
      break;
    case "pdf-requestsignatures-overlay":
      shouldRenderDropin = getShouldRenderPdfRequestSignaturesDropin(stores);
      break;
    case "esign-overlay":
      shouldRenderDropin = getShouldRenderESignOverlayDropin(stores);
      break;
    case "edit":
      shouldRenderDropin =
        getShouldRenderEditDropin(stores) ===
        EDIT_DROPIN_TYPE.FULL; /* This is wrong. We must make observable in overlayStore and do that. Will be covered in subscription checks*/
      break;
    case "crop":
      shouldRenderDropin = getShouldRenderCropPagesDropin(stores);
      break;
    case "number-pages":
      shouldRenderDropin = getShouldRenderNumberPagesDropin(stores);
      break;
    case "partial-edit":
      shouldRenderDropin = getShouldRenderEditDropin(stores) === EDIT_DROPIN_TYPE.PARTIAL;
  }
  return shouldRenderDropin;
};

export const getPageOverlayApis = (stores, client) => {
  const overlay = getPageOverlays(stores).find(({ clientName }) => clientName === client);
  if (overlay && Object.keys(overlay).length > 0) {
    return overlay.api;
  }
};

const getOverlayVisibilityMap = stores => stores.overlayDropinStore.overlaysVisibilityMap;

export const getOverlayVisibility = (stores, clientName) => {
  const overlayMap = getOverlayVisibilityMap(stores);
  return overlayMap.get(clientName);
};

export const getOverlayUpdatedTimestamp = stores => stores.overlayDropinStore.overlayUpdatedTimestamp;

export const isEditActive = stores =>
  getPageOverlays(stores).some(overlay => overlay && overlay.clientName === EDIT_CLIENT_NAME);

export const getEditDropinMountedPromise = stores => stores.overlayDropinStore.editDropinMountedPromise;

export const getEnableImageSelection = stores =>
  !getIsMultiDocModeEnabled(stores) && stores.overlayDropinStore.shouldEnableImageSelection;

export const getShouldDeferLoadOverlays = stores => stores.overlayDropinStore.shouldDeferLoadOverlays;
